import { useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { api } from "../api/client";
import { useMutation } from "../hooks/useResource";
import { ErrorBanner } from "../components/common";
import type { PortalLead } from "./types";

/**
 * "What happened to this lead?" — the only write a client makes.
 *
 * The lead id is the whole address of the write: the client id comes from the
 * session, and the server refuses a (lead, client) pair that was never routed
 * together. So there is nothing here to scope and nothing to get wrong.
 *
 * One dialog for the feed and the detail screen. Reporting from the list is the
 * common case — a client works down yesterday's rows marking who they reached —
 * and making that a page navigation each time is what stops it happening at all.
 */

/** What each outcome means to the person picking it, not to the funnel. */
const OUTCOME_HINT: Record<string, string> = {
  delivered: "Received, nothing done yet.",
  contacted: "You reached the owner or manager.",
  quoted: "You gave them a price.",
  won: "You booked the job.",
  lost: "They went elsewhere, or said no.",
  bad_lead: "Wrong property, wrong contact, or not a real opportunity.",
};

export function PortalOutcomeDialog({
  lead,
  outcomes,
  onClose,
  onSaved,
}: {
  lead: Pick<PortalLead, "id" | "address" | "org" | "outcome" | "outcomeNote" | "outcomeValueUsd">;
  outcomes: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [outcome, setOutcome] = useState(lead.outcome ?? "contacted");
  const [note, setNote] = useState(lead.outcomeNote ?? "");
  const [value, setValue] = useState(lead.outcomeValueUsd != null ? String(lead.outcomeValueUsd) : "");

  // An outcome the server knows but this build's list does not would otherwise
  // render as an empty select and be silently replaced on save.
  const options = outcomes.includes(outcome) ? outcomes : [outcome, ...outcomes];
  const numeric = value.trim() === "" ? null : Number(value);
  const badValue = numeric !== null && (!Number.isFinite(numeric) || numeric < 0);

  const report = useMutation(async () => {
    await api.post(`/api/portal/leads/${lead.id}/outcome`, {
      outcome,
      // Omitted rather than sent empty: the body is strict and an empty note is
      // not a note.
      ...(note.trim() === "" ? {} : { note: note.trim() }),
      ...(numeric === null ? {} : { valueUsd: numeric }),
    });
  });

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Report an outcome</DialogTitle>
      <DialogContent>
        <ErrorBanner error={report.error} />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {lead.address ?? lead.org ?? "This lead"}
        </Typography>
        <Stack spacing={2}>
          <TextField
            select
            label="Outcome"
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
            helperText={OUTCOME_HINT[outcome] ?? "Reporting again replaces the previous answer."}
            fullWidth
          >
            {options.map((o) => (
              <MenuItem key={o} value={o}>
                {o}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Job value (USD)"
            value={value}
            onChange={(e) => setValue(e.target.value.replace(/[^\d.]/g, ""))}
            error={badValue}
            helperText={
              outcome === "won"
                ? "What the job is worth. This is what makes the scoreboard mean anything."
                : "Optional."
            }
            sx={{ width: 200 }}
          />
          <TextField
            label="Note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            minRows={3}
            inputProps={{ maxLength: 2000 }}
            helperText="Optional. Visible to whoever manages your account."
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={report.pending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={outcome === "" || badValue || report.pending}
          onClick={() => void report.run(undefined).then((ok) => ok && onSaved())}
        >
          {report.pending ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
